import { paymentStatusLabel, paymentStatusOptions } from "helpers/paymentLifecycle";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { gameCenterLimits } from "configs/limits";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  handleDepositsPaginate,
  getDepositsList,
} from "store/players/list/slice";

import { NotificationTypes, showNotification } from "store/notifications/slice";
import Pagination from "components/Pagination";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import EmptyState from "components/EmptyState/EmptyState";
import { formatTime, useTimeFormat } from "helpers/timeFormat";
import Loader from "components/global/Loader";

type DepositsTableProps = {
  canUpdate: boolean;
};

const DepositsTable: React.FC<DepositsTableProps> = ({ canUpdate }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const timeFormat = useTimeFormat();

  const depositsList = useAppSelector((store) => store.players.depositsList);
  const totalItems = useAppSelector(
    (store) => store.players.totalItemsDeposits,
  );
  const totalPages = useAppSelector(
    (store) => store.players.totalPagesDeposits,
  );
  const activePage = useAppSelector(
    (store) => store.players.activePageDeposits,
  );
  const statusesList = useAppSelector((store) => store.players.statusesList);
  const [processingDepositId, setProcessingDepositId] = useState<number>();

  const isFetched = useAppSelector((store) => store.players.isDepositsFetched);

  const [limit, setLimit] = useState(gameCenterLimits[0]);
  const userId = searchParams.get("entityId");

  useEffect(() => {
    if (!userId) return;

    dispatch(
      getDepositsList({
        id: Number(userId),
        limit,
        offset: activePage - 1,
      }),
    );
  }, [activePage, limit, userId]);

  const handleChangeStatus = async (id: number, status: number) => {
    if (!canUpdate) return;

    try {
      setProcessingDepositId(id);

      await instance.put(`${apiRoutes.paymentDepositChangeStatus}/${id}`, {
        statusId: status,
      });

      dispatch(
        showNotification({
          title: t("success"),
          message: t("statusChanged"),
          type: NotificationTypes.success,
        }),
      );

      const userId = searchParams.get("entityId");
      if (!userId) return;

      dispatch(
        getDepositsList({
          id: Number(userId),
          limit,
          offset: activePage - 1,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("statusChangeError"),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      setProcessingDepositId(undefined);
    }
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handleDepositsPaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handleDepositsPaginate(activePage + 1));
    }
  };

  return (
    <div className="player-content-wrapper">
      {!isFetched ? (
        <ModalTabSkeleton />
      ) : depositsList.length === 0 ? (
        <EmptyState message={t("noPlayerDepositsFound")} />
      ) : (
        <>
          <div className="inner-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>
                    {t("attempt")}/{t("paid")}
                  </th>
                  <th>{t("fee")}</th>
                  <th>{t("currency")}</th>
                  <th>{t("method")}</th>
                  <th>{t("provider")}</th>
                  <th>{t("status")}</th>
                  <th>{t("created")}</th>
                </tr>
              </thead>

              <tbody>
                {depositsList.map((row) => {
                  const statusLabel = row.canonical_status;
                  const statusOptions = paymentStatusOptions(row, statusesList);
                  const isDisabled = !canUpdate || !row.allowed_status_actions?.length;

                  return (
                    <tr
                      key={row.id}
                      className={
                        processingDepositId === row.id
                          ? "modal-table-processing-row"
                          : ""
                      }
                    >
                      <td>{row.id}</td>

                      <td>
                        {row.amountAttempt}/{row.amountPaid}
                      </td>

                      <td>{row.fee}</td>

                      <td>{row.currency}</td>

                      <td>{row.paymentMethod}</td>

                      <td>
                        {row.paymentProviderImage && (
                          <img
                            className="finance-provider-logo"
                            src={row.paymentProviderImage}
                            alt=""
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                      </td>

                      <td>
                        <div
                          className={`table-status-dropdown table-status-dropdown--deposits table-status-dropdown--${statusLabel}`}
                        >
                          <DefaultDropdown
                            value={row.canonical_status}
                            selectedLabel={paymentStatusLabel(row, statusesList)}
                            options={statusOptions}
                            isSmall
                            disabled={isDisabled}
                            onSelect={(value) => {
                              if (!row.allowed_status_actions?.includes(String(value))) return;
                              const statusId = statusesList.find(item => item.value === value)?.statusId;
                              if (statusId != null) handleChangeStatus(row.id, statusId);
                            }}
                          />
                        </div>
                      </td>

                      <td className="date">
                        <span>{moment(row.created).format("DD.MM.YYYY")}</span>
                        <span>{formatTime(row.created, timeFormat)}</span>
                      </td>

                      {processingDepositId === row.id && (
                        <div className="modal-table-processing-spinner">
                          <Loader />
                        </div>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {depositsList.length > 0 && (
            <Pagination
              limit={limit}
              activePage={activePage}
              totalItems={totalItems}
              totalPages={totalPages}
              limitsArray={gameCenterLimits}
              handleLimitClick={(nextLimit) => {
                setLimit(nextLimit);
                dispatch(handleDepositsPaginate(1));
              }}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePrevClick}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DepositsTable;
